/**
 * Error Page
 * Shown when payment fails
 * Displays error details and troubleshooting options
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckoutSession } from '../hooks';
import { safeRedirect } from '../lib/validation';

interface ErrorDetails {
  code?: string;
  message?: string;
  description?: string;
}

export default function ErrorPage() {
  const { t, i18n } = useTranslation();
  const [errorDetails, setErrorDetails] = useState<ErrorDetails>({});

  // Extract sessionId from URL path
  const pathParts = window.location.pathname.split('/');
  const sessionId = pathParts[pathParts.length - 1] || '';

  // Extract organizationId from query params
  const searchParams = new URLSearchParams(window.location.search);
  const organizationId = searchParams.get('organization_id') || undefined;

  const { session } = useCheckoutSession(sessionId, organizationId);

  useEffect(() => {
    // Set language from session
    if (session?.language && session.language !== i18n.language) {
      i18n.changeLanguage(session.language);
    }
  }, [session, i18n]);

  // Extract and set error details from URL params and session
  useEffect(() => {
    if (session?.error_code || session?.error_message) {
      setErrorDetails({
        code: session.error_code || undefined,
        message: session.error_message || undefined,
        description: getErrorDescription(session.error_code),
      });
    }
  }, [session]);

  // Get user-friendly error description based on error code
  const getErrorDescription = (code?: string | null): string => {
    if (!code) return '';

    const descriptions: Record<string, string> = {
      'card_declined': t('error.desc.cardDeclined') || 'Tu tarjeta fue rechazada por el banco. Intenta con otra tarjeta.',
      'card_expired': t('error.desc.cardExpired') || 'Tu tarjeta ha expirado. Por favor utiliza una tarjeta válida.',
      'insufficient_funds': t('error.desc.insufficientFunds') || 'Fondos insuficientes en la tarjeta.',
      'invalid_card': t('error.desc.invalidCard') || 'Los datos de la tarjeta no son válidos.',
      'card_cancelled': t('error.desc.cardCancelled') || 'El usuario canceló el registro de tarjeta.',
      'bank_rejected': t('error.desc.bankRejected') || 'El banco rechazó la transacción.',
      'processing_error': t('error.desc.processingError') || 'Hubo un error al procesar tu transacción.',
      'server_error': t('error.desc.serverError') || 'Error en nuestro servidor. Por favor intenta más tarde.',
    };

    return descriptions[code] || (t('error.desc.generic') || 'Hubo un problema al procesar tu pago.');
  };

  // Handle redirect to return URL with validation
  const handleReturnToSaaS = () => {
    const params = new URLSearchParams(window.location.search);
    let returnUrl = params.get('returnUrl');

    if (!returnUrl && session?.return_url) {
      returnUrl = session.return_url;
    }

    // Use safe redirect that validates URL against whitelist
    safeRedirect(returnUrl, () => {
      window.history.back();
    });
  };

  const handleRetry = () => {
    if (sessionId) {
      window.location.href = `/checkout/${sessionId}`;
    } else {
      window.history.back();
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 py-8">
      <div className="card w-full">
        {/* Error Icon & Title */}
        <div className="text-center mb-8">
          <div className="status-icon-container status-icon-error mb-6">
            <svg
              className="h-8 w-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('error.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('error.message')}
          </p>
        </div>

        {/* Error Details */}
        {errorDetails.message && (
          <div className="space-y-6 mb-8">
            {/* Error Message */}
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-800">
              <h2 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-3">
                {t('error.details')}
              </h2>
              <p className="text-red-800 dark:text-red-300 font-medium mb-2">
                {errorDetails.message}
              </p>
              {errorDetails.description && (
                <p className="text-red-700 dark:text-red-400 text-sm">
                  {errorDetails.description}
                </p>
              )}
            </div>

            {/* Error Code (for debugging) */}
            {errorDetails.code && (
              <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4 border border-gray-200 dark:border-gray-800">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('error.code')}</p>
                <p className="font-mono text-sm text-gray-900 dark:text-white">
                  {errorDetails.code}
                </p>
              </div>
            )}

            {/* Session ID (for support) */}
            {sessionId && (
              <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4 border border-gray-200 dark:border-gray-800">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('error.sessionId')}</p>
                <p className="font-mono text-xs text-gray-900 dark:text-white break-all">
                  {sessionId}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Troubleshooting Tips */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800 mb-8">
          <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-3">
            {t('error.troubleshooting')}
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-2 list-disc list-inside">
            <li>{t('error.tip1') || 'Verifica que los datos de tu tarjeta sean correctos'}</li>
            <li>{t('error.tip2') || 'Asegúrate de tener fondos disponibles'}</li>
            <li>{t('error.tip3') || 'Intenta con otra tarjeta si la problema persiste'}</li>
            <li>{t('error.tip4') || 'Contacta a tu banco si sigue fallando'}</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleRetry}
            className="btn-primary w-full py-3"
          >
            {t('error.tryAgain')}
          </button>
          <button
            onClick={handleReturnToSaaS}
            className="btn-secondary w-full py-3"
          >
            {session?.return_url ? (t('error.returnToApp') || 'Volver a la aplicación') : t('error.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
